import { Appointment, BookingState } from "core/domain/booking/BookingState";
import { strings } from "core/strings";
import { guid } from "core/types";
import { StorageKey } from "store/Storage";

import { DateTime } from "@bps/date-time";

import {
  ConfirmedReservation,
  UnconfirmedReservation
} from "../../domain/booking/booking.types";

type StoredReservation = Omit<
  ConfirmedReservation,
  "startTime" | "endTime" | "timeZone" | "supportStartTime" | "supportEndTime"
> & {
  startTime: string;
  endTime: string;
  supportStartTime?: string;
  supportEndTime?: string;
  timeZone: string;
};

type StoredAppointment = Omit<
  Appointment,
  "startTime" | "endTime" | "timeZone"
> & {
  startTime: string;
  endTime: string;
  timeZone: string;
};

type StoredUnconfirmedReservation = Omit<
  UnconfirmedReservation,
  "startTime" | "endTime"
> & {
  startTime: string;
  endTime: string;
};

type StoredWizardState = Omit<
  BookingState,
  | "appointmentStartTime"
  | "reservation"
  | "appointment"
  | "unconfirmedReservation"
> & {
  timeSlot?: string;
  reservation?: StoredReservation;
  appointment?: StoredAppointment;
  unconfirmedReservation?: StoredUnconfirmedReservation;
  lastUpdatedMillis: number;
};

const bookingTimeoutMills = 15 * 60 * 1000;

const clearStateFromLocalStorage = () => {
  localStorage.removeItem(StorageKey.BHB_WIZARD_MODEL);
  return undefined;
};

// Stored booking state can include personal details, so remove it once it has
// expired, even if the patient never comes back to the booking pages.
const clearExpiredStateFromLocalStorage = () => {
  const json = localStorage.getItem(StorageKey.BHB_WIZARD_MODEL);
  if (!json) return;

  try {
    const { lastUpdatedMillis } = JSON.parse(json) as StoredWizardState;
    if (
      !lastUpdatedMillis ||
      lastUpdatedMillis <= Date.now() - bookingTimeoutMills
    ) {
      clearStateFromLocalStorage();
    }
  } catch {
    clearStateFromLocalStorage();
  }
};

const toLocalStorageReservation = (
  reservation?: ConfirmedReservation
): StoredReservation | undefined => {
  if (!reservation) return undefined;

  const { startTime, endTime, supportEndTime, supportStartTime } = reservation;
  return {
    ...reservation,
    startTime: startTime._dateTime.toString(),
    endTime: endTime._dateTime.toString(),
    supportStartTime: supportStartTime?._dateTime.toString(),
    supportEndTime: supportEndTime?._dateTime.toString(),
    timeZone: startTime.timeZone.name
  };
};

const toLocalStorageAppointment = (
  appt?: Appointment
): StoredAppointment | undefined => {
  if (!appt) return undefined;

  const { startTime, endTime } = appt;
  return {
    ...appt,
    startTime: startTime._dateTime.toString(),
    endTime: endTime._dateTime.toString(),
    timeZone: startTime.timeZone.name
  };
};

const toLocalStorageUnConfirmedReservation = (
  reservation?: UnconfirmedReservation
): StoredUnconfirmedReservation | undefined => {
  if (!reservation) return undefined;

  const { startTime, endTime } = reservation;
  return {
    ...reservation,
    startTime: startTime._dateTime.toString(),
    endTime: endTime._dateTime.toString(),
    timeZone: startTime.timeZone.name
  };
};

const storeStateInLocalStorage = (state?: BookingState) => {
  if (state) {
    // The DateTime.toJSON() method is called when serializing to JSON, which actually calls toISO() and creates a string.
    // Note that some information is lost in the process (the locale, the zone (but not its offset) are for instance not serialized).
    // This code below stores the reservation timeZone, so we can persist this information post page refresh.

    const localStorageState: StoredWizardState = {
      ...state,
      // Free-text notes can contain health information, so keep them in memory
      // only. They are entered after login and are cheap to retype.
      patientNotes: undefined,
      reservation: toLocalStorageReservation(state.reservation),
      unconfirmedReservation: toLocalStorageUnConfirmedReservation(
        state.unconfirmedReservation
      ),
      appointment: toLocalStorageAppointment(state.appointment),
      lastUpdatedMillis: new Date().valueOf()
    };

    localStorage.setItem(
      StorageKey.BHB_WIZARD_MODEL,
      JSON.stringify({
        ...localStorageState
      })
    );
  } else {
    clearStateFromLocalStorage();
  }
};

const restoreStateFromLocalStorage = (
  currentPracticeId: guid
): BookingState | undefined => {
  // Rehydrate the return parameters from local storage when the store is constructed
  const json = localStorage.getItem(StorageKey.BHB_WIZARD_MODEL);
  if (!json) return undefined;

  const {
    practiceId,
    lastUpdatedMillis,
    timeSlot: timeSlotIso,
    reservation,
    unconfirmedReservation,
    appointment,
    ...modelRest
  } = JSON.parse(json) as StoredWizardState;

  // If the practiceId stored in the state is different to the current practice, then throw out the state.
  if (!strings.areEqual(currentPracticeId, practiceId)) {
    return clearStateFromLocalStorage();
  }

  // If the booking is stale, then throw out the state.
  const now = DateTime.now();
  if (
    lastUpdatedMillis &&
    lastUpdatedMillis <= now.valueOf() - bookingTimeoutMills
  ) {
    return clearStateFromLocalStorage();
  }

  // If the time slot has expired, then throw out the state.
  const timeSlot = timeSlotIso ? DateTime.fromISO(timeSlotIso) : undefined;
  if (timeSlot && timeSlot <= now) {
    return clearStateFromLocalStorage();
  }

  const model: BookingState = { ...modelRest, practiceId };

  if (reservation) {
    const {
      startTime,
      endTime,
      timeZone,
      supportStartTime,
      supportEndTime,
      ...reservationRest
    } = reservation;

    model.reservation = {
      ...reservationRest,
      startTime: DateTime.fromISO(startTime, { zone: timeZone }), // Restore timeZone property for display purposes
      endTime: DateTime.fromISO(endTime, { zone: timeZone }), // Restore timeZone property for display purposes,
      supportStartTime: supportStartTime
        ? DateTime.fromISO(supportStartTime, { zone: timeZone })
        : undefined,
      supportEndTime: supportEndTime
        ? DateTime.fromISO(supportEndTime, { zone: timeZone })
        : undefined
    };
  }

  if (unconfirmedReservation) {
    const { startTime, endTime, timeZone, ...rest } = unconfirmedReservation;

    model.unconfirmedReservation = {
      ...rest,
      startTime: DateTime.fromISO(startTime, { zone: timeZone }), // Restore timeZone property for display purposes
      endTime: DateTime.fromISO(endTime, { zone: timeZone }) // Restore timeZone property for display purposes
    };
  }

  if (appointment) {
    const { startTime, endTime, timeZone } = appointment;

    model.appointment = {
      ...appointment,
      startTime: DateTime.fromISO(startTime, { zone: timeZone }),
      endTime: DateTime.fromISO(endTime, { zone: timeZone })
    };
  }

  return model;
};

export const BookingStorageService = {
  storeState: storeStateInLocalStorage,
  restoreState: restoreStateFromLocalStorage,
  clearExpiredState: clearExpiredStateFromLocalStorage
};
